import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import SignInMethodPanel from '@/components/settings/SignInMethodPanel';
import { SupportResources } from '@/components/support/SupportResources';
import { ViewAsConsentPanel } from '@/components/ViewAsConsentPanel';
import { Lock, User, Mail, Edit2, Save, X, Bell, AlertTriangle, XCircle, Phone, MessageCircle, ExternalLink, Camera, Globe, Compass, Users, Zap, CreditCard } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import ProfilePicture from '@/components/ProfilePicture';
import ProfilePictureUpload from '@/components/ProfilePictureUpload';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import DeleteAccountCard from '@/components/DeleteAccountCard';
import CancelMembershipCard from '@/components/CancelMembershipCard';
import DripRemovalModal from '@/components/DripRemovalModal';
import { ConnectionsSettings } from '@/components/ConnectionsSettings';
import { useLanguage } from '@/contexts/LanguageContext';

const ORIENTATION_FIELDS = [
  {
    key: 'focus',
    label: 'My Focus Right Now',
    examples: ['Understanding myself more deeply.', 'Healing old emotional patterns.', 'Strengthening my intuition.', 'Learning to stay grounded.'],
  },
  {
    key: 'experience',
    label: 'What I Want to Experience',
    examples: ['Clarity and direction.', 'A deeper connection to my inner self.', 'More emotional balance.', 'Tools to navigate my growth.'],
  },
  {
    key: 'show_up',
    label: 'How I Show Up Best',
    examples: ['I learn through reflection.', 'I grow through conversation.', 'I thrive with structure.', 'I need space to process.'],
  },
  {
    key: 'affirmation',
    label: 'Affirmation (optional)',
    examples: ['I show up here as myself.', 'I\u2019m committed to my growth.', 'I\u2019m here with intention.'],
  },
];

const SettingsPage = ({ user }) => {
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const [languageOptions, setLanguageOptions] = useState([{ code: 'en', name: 'English' }]);
  const [profileForm, setProfileForm] = useState({
    username: user.username,
    full_name: user.full_name,
    email: user.email
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [notificationPrefs, setNotificationPrefs] = useState({
    new_content: true,
    folder_unlocked: true,
    live_stream: true,
    calendar_event: true,
    forum_reply: true,
    admin_message: true,
    announcements: true,
    email_notifications: true,
    mention_email: 'instant',
    monthly_digest: true
  });
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [orientation, setOrientation] = useState({
    focus: user?.personal_orientation?.focus || '',
    experience: user?.personal_orientation?.experience || '',
    show_up: user?.personal_orientation?.show_up || '',
    affirmation: user?.personal_orientation?.affirmation || '',
  });
  const [showOrientation, setShowOrientation] = useState(user?.show_orientation !== false);
  const [orientationLoading, setOrientationLoading] = useState(false);
  const [showProfileUpload, setShowProfileUpload] = useState(false);
  const [profilePicture, setProfilePicture] = useState(user?.profile_picture || null);
  
  const [membership, setMembership] = useState(null);
  const [showDripModal, setShowDripModal] = useState(false);

  const fetchMembership = async () => {
    try {
      const res = await axios.get(`${API}/user/membership`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setMembership(res.data);
    } catch (e) {
      // Membership card simply stays hidden if this fails
    }
  };

  useEffect(() => {
    fetchMembership();
    fetchNotificationPreferences();
    axios.get(`${API}/i18n/languages`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => setLanguageOptions(r.data.languages || []))
      .catch(() => {});
  }, []);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    // Validation
    if (!profileForm.username || !profileForm.full_name || !profileForm.email) {
      toast.error('All fields are required');
      return;
    }

    if (!profileForm.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setProfileLoading(true);

    try {
      const response = await axios.put(`${API}/auth/profile`, {
        username: profileForm.username,
        full_name: profileForm.full_name,
      });

      toast.success('Profile updated successfully!');
      
      // Update localStorage with new user data
      const updatedUser = response.data.user;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setIsEditingProfile(false);
      
      // Refresh page to update user data everywhere
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setProfileForm({
      username: user.username,
      full_name: user.full_name,
      email: user.email
    });
    setIsEditingProfile(false);
  };

  const fetchNotificationPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationPrefs(response.data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const handleUpdateNotificationPreferences = async () => {
    setPrefsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/notifications/preferences`, notificationPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notification preferences updated!');
    } catch (error) {
      toast.error('Failed to update preferences');
    } finally {
      setPrefsLoading(false);
    }
  };

  const handleUpdateOrientation = async () => {
    setOrientationLoading(true);
    try {
      const response = await axios.put(`${API}/auth/personal-orientation`, {
        ...orientation,
        show_orientation: showOrientation,
      });
      const updatedUser = response.data.user;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      toast.success('Personal orientation saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save personal orientation');
    } finally {
      setOrientationLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!passwordForm.old_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      toast.error('All password fields are required');
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password
      });

      toast.success('Password changed successfully!');
      
      // Reset form
      setPasswordForm({
        old_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">Account Settings</h1>
        <p className="text-lg text-gray-300">Manage your account preferences and security</p>
      </div>

      <div className="space-y-6">
        {/* Anyone asking to see this account, and the answer — hidden unless there is one */}
        <ViewAsConsentPanel />

        {/* Profile Picture */}
        <CollapsibleCard title="Profile Picture" icon={Camera} defaultOpen testId="settings-profile-picture" description="Your profile picture will appear in the sidebar, forum posts, and messages">
          <CardContent>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="relative group">
                <ProfilePicture
                  src={profilePicture}
                  alt={user.full_name}
                  size="2xl"
                />
                <div 
                  className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  onClick={() => setShowProfileUpload(true)}
                >
                  <Camera className="w-8 h-8 text-white" />
                </div>
              </div>
              
              <div className="flex-1 space-y-3 text-center sm:text-left">
                <p className="text-sm text-gray-600">
                  Upload a profile picture to personalize your account. 
                  Accepted formats: JPG, PNG, WebP. Max size: 5MB.
                </p>
                <Button
                  onClick={() => setShowProfileUpload(true)}
                  className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {profilePicture ? 'Change Picture' : 'Upload Picture'}
                </Button>
              </div>
            </div>
          </CardContent>
        </CollapsibleCard>

        {/* Account Information */}
        <CollapsibleCard
          title="Account Information"
          icon={User}
          defaultOpen
          testId="settings-account-info"
          description="Your account details"
          headerAction={!isEditingProfile ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditingProfile(true)}
              className="flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Edit Profile
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelEdit}
                className="flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleUpdateProfile}
                disabled={profileLoading}
                className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-purple-500"
              >
                <Save className="w-4 h-4" />
                {profileLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        >
          <CardContent className="space-y-4">
            {isEditingProfile ? (
              <form className="space-y-4">
                <div>
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    value={profileForm.username}
                    onChange={(e) => setProfileForm({ ...profileForm, username: e.target.value })}
                    placeholder="Enter your username"
                    className="mt-1"
                    data-testid="settings-username-input"
                  />
                </div>
                <div>
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    value={profileForm.full_name}
                    onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                    placeholder="Enter your full name"
                    className="mt-1"
                    data-testid="settings-full-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="username"
                    value={profileForm.email}
                    readOnly
                    disabled
                    className="mt-1 bg-gray-100 cursor-not-allowed"
                    data-testid="settings-email-readonly"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Your email is how you log in, so it can&apos;t be changed here. Contact support and
                    we&apos;ll move your account across.
                  </p>
                </div>
              </form>
            ) : (
              <>
                <div>
                  <Label className="text-gray-600">Username</Label>
                  <p className="text-lg font-medium">{user.username}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Full Name</Label>
                  <p className="text-lg font-medium">{user.full_name}</p>
                </div>
                <div>
                  <Label className="text-gray-600 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </Label>
                  <p className="text-lg font-medium">{user.email}</p>
                </div>
              </>
            )}
            <div>
              <Label className="text-gray-600">Membership Type</Label>
              <p className="text-lg font-medium capitalize">
                {user.membership_type === 'free_comp' ? 'Complimentary' : user.membership_type}
              </p>
            </div>
          </CardContent>
        </CollapsibleCard>

        {/* Personal Orientation */}
        <CollapsibleCard
          title="Personal Orientation"
          icon={Compass}
          testId="settings-personal-orientation"
          description="A space to express who you are and why you’re here — in your own words, without labels, categories, or personal details."
        >
          <CardContent className="space-y-6">
            {ORIENTATION_FIELDS.map((field) => (
              <div key={field.key}>
                <Label htmlFor={`orientation-${field.key}`} className="text-base">{field.label}</Label>
                <Textarea
                  id={`orientation-${field.key}`}
                  data-testid={`orientation-${field.key}-input`}
                  value={orientation[field.key]}
                  maxLength={300}
                  onChange={(e) => setOrientation({ ...orientation, [field.key]: e.target.value })}
                  placeholder="Write your statement..."
                  className="mt-1.5 min-h-[70px]"
                />
                <div className="mt-1.5 space-y-0.5">
                  {field.examples.map((ex, i) => (
                    <p key={i} className="text-xs text-gray-400 italic">e.g. “{ex}”</p>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between border-t pt-4">
              <div>
                <Label className="text-base">Show on my profile card</Label>
                <p className="text-sm text-gray-500">Let other members see this when they view your profile</p>
              </div>
              <Switch
                data-testid="orientation-visibility-switch"
                checked={showOrientation}
                onCheckedChange={setShowOrientation}
              />
            </div>

            <p className="text-sm text-center text-purple-600">
              ✨ Review your personal orientation as you move through the coaching program and change as you transform.
            </p>

            <Button
              onClick={handleUpdateOrientation}
              disabled={orientationLoading}
              data-testid="save-orientation-btn"
              className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600"
            >
              {orientationLoading ? 'Saving...' : 'Save Personal Orientation'}
            </Button>
          </CardContent>
        </CollapsibleCard>

        {/* Connections */}
        <CollapsibleCard title="Connections" icon={Users} testId="settings-connections" description="Manage your connection requests and connected members">
          <CardContent>
            <ConnectionsSettings />
          </CardContent>
        </CollapsibleCard>

        {/* How they sign in */}
        <CollapsibleCard title="How You Sign In" icon={Lock} testId="settings-sign-in-methods"
          description="Use your password, Google, or both">
          <SignInMethodPanel />
        </CollapsibleCard>

        {/* Change Password */}
        <CollapsibleCard title="Change Password" icon={Lock} testId="settings-change-password" description="Update your password to keep your account secure">
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label htmlFor="old_password">Current Password</Label>
                <Input
                  id="old_password"
                  name="current-password"
                  type="password"
                  autoComplete="current-password"
                  value={passwordForm.old_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })}
                  placeholder="Enter your current password"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  placeholder="Enter your new password"
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Must be at least 8 characters with uppercase, lowercase, and number
                </p>
              </div>

              <div>
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  name="confirm-new-password"
                  type="password"
                  autoComplete="new-password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  placeholder="Confirm your new password"
                  className="mt-1"
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600"
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </CollapsibleCard>

        {/* Suicide Hotline Notice */}
        <CollapsibleCard
          title="Need Support?"
          icon={Phone}
          testId="settings-need-support"
          className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-blue-50"
          titleClassName="text-purple-900"
          description="If you're experiencing a difficult time while going through this material and need immediate support"
        >
          <CardContent>
            <div className="rounded-lg bg-white p-4 border-l-4 border-purple-600">
              <SupportResources />
            </div>
          </CardContent>
        </CollapsibleCard>

        {/* Preferred Language */}
        <CollapsibleCard title="Preferred Language" icon={Globe} testId="language-settings-card" description="Show the app interface and content in your language">
          <CardContent>
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label className="text-base">Language</Label>
                <p className="text-sm text-gray-500">Applies across the whole platform</p>
              </div>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="w-56" data-testid="preferred-language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent data-no-translate>
                  {languageOptions.map((l) => (
                    <SelectItem key={l.code} value={l.code} data-testid={`preferred-language-${l.code}`}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </CollapsibleCard>

        {/* Notification Preferences */}
        <CollapsibleCard title="Notification Preferences" icon={Bell} testId="settings-notifications" description="Choose which notifications you want to receive">
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">New Content Available</Label>
                <p className="text-sm text-gray-500">When new modules or content are added</p>
              </div>
              <Switch
                checked={notificationPrefs.new_content}
                onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, new_content: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Folder Unlocked</Label>
                <p className="text-sm text-gray-500">When a folder becomes accessible (drip-feed)</p>
              </div>
              <Switch
                checked={notificationPrefs.folder_unlocked}
                onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, folder_unlocked: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Live Stream Alerts</Label>
                <p className="text-sm text-gray-500">15 minutes before a live stream starts</p>
              </div>
              <Switch
                checked={notificationPrefs.live_stream}
                onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, live_stream: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">New Calendar Events</Label>
                <p className="text-sm text-gray-500">When a new event is added to the calendar</p>
              </div>
              <Switch
                checked={notificationPrefs.calendar_event}
                onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, calendar_event: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Forum Replies</Label>
                <p className="text-sm text-gray-500">When someone replies to your post</p>
              </div>
              <Switch
                checked={notificationPrefs.forum_reply}
                onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, forum_reply: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Admin Messages</Label>
                <p className="text-sm text-gray-500">When admin replies to your message</p>
              </div>
              <Switch
                checked={notificationPrefs.admin_message}
                onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, admin_message: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Announcements</Label>
                <p className="text-sm text-gray-500">Important announcements from admin</p>
              </div>
              <Switch
                checked={notificationPrefs.announcements}
                onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, announcements: checked })}
              />
            </div>

            <div className="flex items-center justify-between gap-4">
              <div>
                <Label className="text-base">@Mention Emails</Label>
                <p className="text-sm text-gray-500">How to be emailed when someone @mentions you</p>
              </div>
              <Select
                value={notificationPrefs.mention_email}
                onValueChange={(value) => setNotificationPrefs({ ...notificationPrefs, mention_email: value })}
              >
                <SelectTrigger className="w-44" data-testid="mention-email-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="instant" data-testid="mention-email-instant">Every mention</SelectItem>
                  <SelectItem value="daily" data-testid="mention-email-daily">Once per day</SelectItem>
                  <SelectItem value="off" data-testid="mention-email-off">Don't email me</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Monthly Digest</Label>
                <p className="text-sm text-gray-500">A monthly email of new arrivals, workshops & spotlights</p>
              </div>
              <Switch
                data-testid="monthly-digest-switch"
                checked={notificationPrefs.monthly_digest}
                onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, monthly_digest: checked })}
              />
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Email Notifications</Label>
                  <p className="text-sm text-gray-500">Receive notifications via email</p>
                </div>
                <Switch
                  checked={notificationPrefs.email_notifications}
                  onCheckedChange={(checked) => 
                    setNotificationPrefs({ ...notificationPrefs, email_notifications: checked })
                  }
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">You can unsubscribe from email notifications at any time by turning this off</p>
            </div>

            <Button 
              onClick={handleUpdateNotificationPreferences}
              disabled={prefsLoading}
              className="w-full bg-gradient-to-r from-cyan-500 to-purple-500"
            >
              {prefsLoading ? 'Saving...' : 'Save Preferences'}
            </Button>
          </CardContent>
        </CollapsibleCard>

        {/* Drip Removal Add-On */}
        {membership && !['bronze', 'free', 'free_comp'].includes(membership.membership_tier) &&
          !membership.drip_removal_addon && (
          <CollapsibleCard
            title="Remove Drip Feed"
            icon={Zap}
            testId="settings-drip-removal"
            defaultOpen
            headerAction={(
              <span
                className="rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300
                           px-3 py-1 text-xs font-semibold whitespace-nowrap"
                data-testid="drip-removal-price-badge"
              >
                One-time upgrade
              </span>
            )}
            description="Unlock every folder in your tier straight away, instead of waiting"
          >
            <CardContent className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  By default, folders in the Coaching Program open up as you complete the one before it.
                  This one-time add-on removes that pacing and gives you immediate access to everything
                  included in your {membership.tier_display || membership.membership_tier} membership.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  One-time payment · Permanent · Applies to your current tier
                </p>
              </div>
              <Button
                onClick={() => setShowDripModal(true)}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                data-testid="remove-drip-feed-btn"
              >
                <Zap className="w-4 h-4 mr-2" />
                Remove Drip Feed
              </Button>
            </CardContent>
          </CollapsibleCard>
        )}

        {/* Membership, payments and cancellation all live on one page now. Bronze sees it too —
            it is where they find what each tier costs and what it opens. */}
        {!['free', 'free-comp', 'free_comp'].includes(user.membership_tier) && (
          <CollapsibleCard
            title="My Membership"
            icon={CreditCard}
            testId="settings-my-membership"
            description={user.membership_tier === 'bronze'
              ? 'What each tier costs, what it opens, and how to upgrade'
              : 'Your payments, your upgrade credit, the maintenance fee, and cancelling'}
          >
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-gray-700">
                <p>
                  <strong>Current membership:</strong> {user.membership_tier?.toUpperCase()}
                </p>
                <p className="text-gray-600 mt-2">
                  {user.membership_tier === 'bronze'
                    ? 'See what Silver, Gold and Platinum include, what each one costs in full or month by month, and upgrade whenever you\'re ready.'
                    : 'Everything about your membership money — what you\'ve paid, what\'s scheduled, the credit you hold toward an upgrade, and how to cancel — is on your membership page.'}
                </p>
              </div>
              <Button
                onClick={() => navigate('/dashboard/membership')}
                className="w-full"
                data-testid="settings-open-membership-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {user.membership_tier === 'bronze' ? 'See membership tiers and pricing' : 'Open My Membership'}
              </Button>
            </CardContent>
          </CollapsibleCard>
        )}

        <CancelMembershipCard />

        <DeleteAccountCard />
      </div>

      
      {/* Profile Picture Upload Modal */}
      <ProfilePictureUpload
        currentPicture={profilePicture}
        onUploadSuccess={(newPicture) => setProfilePicture(newPicture)}
        showModal={showProfileUpload}
        onClose={() => setShowProfileUpload(false)}
      />

      <DripRemovalModal
        isOpen={showDripModal}
        onClose={() => setShowDripModal(false)}
        onSuccess={fetchMembership}
        currentTier={membership?.membership_tier || 'silver'}
      />
    </div>
  );
};

export default SettingsPage;
